// 11 §2 / A4 — the Phaser.Game singleton. Scale.NONE (board zoom is a camera concern),
// pixelArt + roundPixels, antialias off. docs/33 E4: the canvas is the window's size in *device*
// pixels, shown at 1/ratio, so text and one-pixel strokes are sharp on HiDPI screens; scenes
// zoom their cameras by `pixelRatio()` to keep working in CSS px. Phaser never creates DOM beyond its canvas; React never
// draws into it; they talk only through the stores.
import Phaser from 'phaser';
import { Boot } from './scenes/Boot';
import { Encounter } from './scenes/Encounter';
import { Gallery } from './scenes/Gallery';
import type { AtlasManifest } from '../config';
import { pixelRatio } from './board/BoardCamera';
import { installGraphicsCache } from './graphicsCache';
import { installParticleCull } from './particleCull';

export interface GameBoot { manifest: AtlasManifest & { source: 'oryx' | 'dev' }; gallery?: boolean }

let game: Phaser.Game | undefined;

export function startGame(parent: HTMLElement, boot: GameBoot): Phaser.Game {
  if (game) return game;
  // docs/147 PG2: a Graphics hands the batch the triangles it made last while nothing about it changed
  installGraphicsCache();
  // docs/147 PG3: an emitter draws only the particles that can reach the camera's view
  installParticleCull();
  const dpr = pixelRatio();
  const width = Math.round(Math.max(320, parent.clientWidth || window.innerWidth) * dpr);
  const height = Math.round(Math.max(240, parent.clientHeight || window.innerHeight) * dpr);
  game = new Phaser.Game({
    type: Phaser.AUTO,
    parent,
    width,
    height,
    backgroundColor: '#0b1020',
    pixelArt: true,
    roundPixels: true,
    antialias: false,
    antialiasGL: false,
    scale: { mode: Phaser.Scale.NONE, autoCenter: Phaser.Scale.NO_CENTER, zoom: 1 / dpr },
    render: { pixelArt: true, roundPixels: true, antialias: false, antialiasGL: false },
    input: { keyboard: !!boot.gallery }, // keyboard is handled by the document-level grammar (core/authoring/keyboard.ts); the gallery reads keys itself
    scene: [Boot, Encounter, Gallery],
    callbacks: {
      preBoot: (g) => { g.registry.set('boot', boot); },
    },
  });
  // Exposed for the Playwright smoke (e2e/smoke.spec.ts waits on registry 'encounterReady').
  (window as unknown as { __fkGame?: Phaser.Game }).__fkGame = game;
  // Scale.NONE never resizes the canvas itself; we follow the window (11 §4.1 note on Scale.NONE).
  const onResize = () => {
    if (!game) return;
    const r = pixelRatio();
    if (game.scale.zoom !== 1 / r) game.scale.setZoom(1 / r);
    game.scale.resize(Math.round(Math.max(320, parent.clientWidth || window.innerWidth) * r), Math.round(Math.max(240, parent.clientHeight || window.innerHeight) * r));
  };
  window.addEventListener('resize', onResize);
  return game;
}

export function getGame(): Phaser.Game | undefined { return game; }
